import { useEffect, useState } from "react";

import {
  CompareArrows as CompareIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
} from "@mui/material";
import {
  listSurveyVersions,
  postSurveyVersionRollback,
} from "@rubi/api-generated/clientRs/sdk.gen";
import type { SurveyVersionModel } from "@rubi/api-generated/clientRs/types.gen";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";

import { formatVersionNumber } from "@/components/online-reporting/form-management/utils";
import { VersionBadge } from "@/components/online-reporting/form-management/VersionBadge";
import { formatVersionDate } from "@/utils/versionUtils";

interface VersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  parentSurveyId: string;
  orgId: string;
  onCompare: (
    fromVersion: SurveyVersionModel,
    toVersion: SurveyVersionModel,
  ) => void;
  onVersionSelect?: (version: SurveyVersionModel) => void;
}

export const VersionHistoryModal = ({
  open,
  onClose,
  parentSurveyId,
  orgId,
  onCompare,
  onVersionSelect,
}: VersionHistoryModalProps) => {
  const [versions, setVersions] = useState<SurveyVersionModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<
    SurveyVersionModel[]
  >([]);
  const [rollbackConfirmVersion, setRollbackConfirmVersion] =
    useState<SurveyVersionModel | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setSelectedForCompare([]);
      setRollbackConfirmVersion(null);
      loadVersions();
    }
  }, [open, parentSurveyId]);

  const loadVersions = async () => {
    setIsLoading(true);
    try {
      const response = await listSurveyVersions({
        path: { org_id: orgId, parent_survey_id: parentSurveyId },
      });

      setVersions(response.data?.versions || []);
    } catch (error) {
      console.error("Error loading version history:", error);
      toast.error("Failed to load version history");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRollbackClick = (version: SurveyVersionModel) => {
    setRollbackConfirmVersion(version);
  };

  const handleRollbackConfirm = async () => {
    if (!rollbackConfirmVersion) return;

    setIsRollingBack(true);
    try {
      await postSurveyVersionRollback({
        path: {
          org_id: orgId,
          parent_survey_id: parentSurveyId,
          version_id: rollbackConfirmVersion.id,
        },
      });

      toast.success(
        `Rolled back to version ${formatVersionNumber(rollbackConfirmVersion)}. Draft has been updated.`,
      );
      setRollbackConfirmVersion(null);
      onClose();
      // Navigate to edit mode to show the newly updated draft
      // Don't call onVersionSelect as that would view the old version
      window.location.reload();
    } catch (error) {
      console.error("Error rolling back version:", error);
      toast.error("Failed to rollback version");
    } finally {
      setIsRollingBack(false);
    }
  };

  const handleRollbackCancel = () => {
    setRollbackConfirmVersion(null);
  };

  const handleCompareSelect = (version: SurveyVersionModel) => {
    if (selectedForCompare.find((v) => v.id === version.id)) {
      setSelectedForCompare(
        selectedForCompare.filter((v) => v.id !== version.id),
      );
    } else if (selectedForCompare.length < 2) {
      setSelectedForCompare([...selectedForCompare, version]);
    }
  };

  const handleCompare = () => {
    if (selectedForCompare.length === 2) {
      onCompare(selectedForCompare[0], selectedForCompare[1]);
      setSelectedForCompare([]);
    }
  };

  const formatVersionWithStatus = (version: SurveyVersionModel) => {
    if (version.status === "draft") {
      return "Draft version";
    }
    const statusLabel =
      version.status === "active"
        ? " (Live)"
        : version.status === "archived"
          ? " (Archived)"
          : "";
    return `v${formatVersionNumber(version)}${statusLabel}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Version History</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Reserve space for comparison bar to prevent layout shift */}
            <Box
              sx={{
                mb: 2,
                minHeight: selectedForCompare.length > 0 ? "auto" : "0px",
                bgcolor: (theme) =>
                  selectedForCompare.length > 0
                    ? alpha(theme.palette.info.main, 0.12)
                    : "transparent",
                borderRadius: 1,
                border: selectedForCompare.length > 0 ? "1px solid" : "none",
                borderColor: "info.light",
                overflow: "hidden",
                transition: "all 0.2s ease-in-out",
              }}
            >
              {selectedForCompare.length === 1 ? (
                <Box
                  sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography
                    variant="body2"
                    color="info.dark"
                    fontWeight="bold"
                  >
                    Selected:
                  </Typography>
                  <VersionBadge
                    version={formatVersionNumber(selectedForCompare[0])}
                    status={
                      selectedForCompare[0].status as
                        | "active"
                        | "draft"
                        | "archived"
                    }
                  />
                  <Typography variant="body2" color="info.dark">
                    {formatVersionWithStatus(selectedForCompare[0])}
                  </Typography>
                </Box>
              ) : selectedForCompare.length === 2 ? (
                <Box sx={{ display: "flex", alignItems: "stretch" }}>
                  {/* Left side */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: alpha(colors.common.black, 0.02),
                      borderRight: "2px solid",
                      borderColor: "info.light",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="info.dark"
                    >
                      LEFT
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VersionBadge
                        version={formatVersionNumber(selectedForCompare[0])}
                        status={
                          selectedForCompare[0].status as
                            | "active"
                            | "draft"
                            | "archived"
                        }
                      />
                      <Typography variant="body2" color="info.dark">
                        {formatVersionWithStatus(selectedForCompare[0])}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="info.dark"
                    >
                      RIGHT
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VersionBadge
                        version={formatVersionNumber(selectedForCompare[1])}
                        status={
                          selectedForCompare[1].status as
                            | "active"
                            | "draft"
                            | "archived"
                        }
                      />
                      <Typography variant="body2" color="info.dark">
                        {formatVersionWithStatus(selectedForCompare[1])}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Box>

            <List sx={{ p: 0 }}>
              {versions.map((version) => {
                const selectedIndex = selectedForCompare.findIndex(
                  (v) => v.id === version.id,
                );
                const isSelected = selectedIndex !== -1;
                const selectionLabel =
                  selectedIndex === 0
                    ? "Left"
                    : selectedIndex === 1
                      ? "Right"
                      : null;

                return (
                  <ListItem
                    key={version.id}
                    onClick={() => handleCompareSelect(version)}
                    sx={{
                      border: "2px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      borderRadius: 1,
                      mb: 1,
                      py: 1,
                      px: 2,
                      cursor: "pointer",
                      bgcolor: isSelected
                        ? "action.selected"
                        : "background.paper",
                      "&:hover": {
                        bgcolor: isSelected
                          ? "action.selected"
                          : "action.hover",
                      },
                    }}
                    secondaryAction={
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {isSelected && selectionLabel && (
                          <Chip
                            label={selectionLabel}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
                          />
                        )}
                        {version.status === "archived" && (
                          <IconButton
                            edge="end"
                            aria-label="rollback"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRollbackClick(version);
                            }}
                            title="Rollback to this version"
                            size="small"
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <VersionBadge
                            version={formatVersionNumber(version)}
                            status={
                              version.status as "active" | "draft" | "archived"
                            }
                          />
                          {version.change_summary && (
                            <Typography variant="body2">
                              {version.change_summary}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          component="span"
                          sx={{ mt: 0.5 }}
                        >
                          {version.status === "draft"
                            ? `Created ${formatVersionDate(version.time_created)}`
                            : `Deployed ${formatVersionDate(version.time_deployed || version.time_created)}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {selectedForCompare.length === 1 && (
          <Button
            onClick={() => {
              onVersionSelect?.(selectedForCompare[0]);
              onClose();
            }}
            variant="contained"
            color="primary"
          >
            Go to Version
          </Button>
        )}
        {selectedForCompare.length === 2 && (
          <Button
            onClick={handleCompare}
            variant="contained"
            startIcon={<CompareIcon />}
          >
            Compare Versions
          </Button>
        )}
      </DialogActions>

      {/* Rollback Confirmation Dialog */}
      <Dialog
        open={!!rollbackConfirmVersion}
        onClose={handleRollbackCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1,
          }}
        >
          <WarningIcon color="warning" />
          Confirm Rollback
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will replace any existing draft with the selected version.
          </Alert>
          {rollbackConfirmVersion && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Are you sure you want to rollback to:
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <VersionBadge
                  version={formatVersionNumber(rollbackConfirmVersion)}
                  status={
                    rollbackConfirmVersion.status as
                      | "active"
                      | "draft"
                      | "archived"
                  }
                />
                <Box>
                  {rollbackConfirmVersion.change_summary && (
                    <Typography variant="body2" fontWeight="bold">
                      {rollbackConfirmVersion.change_summary}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Deployed{" "}
                    {formatVersionDate(
                      rollbackConfirmVersion.time_deployed ||
                        rollbackConfirmVersion.time_created,
                    )}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will create a new draft from this version's configuration.
                You can review the changes before deploying.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRollbackCancel} disabled={isRollingBack}>
            Cancel
          </Button>
          <Button
            onClick={handleRollbackConfirm}
            variant="contained"
            color="warning"
            disabled={isRollingBack}
            startIcon={isRollingBack ? <CircularProgress size={16} /> : null}
          >
            {isRollingBack ? "Rolling back..." : "Rollback"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
